import { Utils } from '@genial/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { Control, useForm } from 'react-hook-form';

import { RedemptionFormData, getValidationCreateSchema } from './constants';

export const useRedemptionController = (minValue: number, maxValue: number) => {
  const [prevValue, setPrevValue] = useState(0);
  const [valueApplied, setValueApplied] = useState(minValue);
  const [isFocused, setIsFocused] = useState(false);

  const tableForm = useForm<RedemptionFormData>({
    mode: 'all',
    reValidateMode: 'onBlur',
    defaultValues: {
      redemptionValue: Utils.Mask.Money(minValue),
    },
    resolver: zodResolver(getValidationCreateSchema(minValue, maxValue)),
  });

  const getRedemptionValueOrMinimum = () => {
    const currentValue = tableForm.getValues('redemptionValue');
    const currentValueUnformatted = Utils.Unmask.Money(currentValue);

    return currentValueUnformatted > minValue ? currentValueUnformatted : minValue;
  };

  const isAbleToChange = (value: number) => {
    return !!(
      minValue &&
      maxValue &&
      prevValue !== value &&
      value >= minValue &&
      value <= maxValue
    );
  };

  const handleSubmit = async () => {
    const redemptionValue = getRedemptionValueOrMinimum();

    tableForm.setValue('redemptionValue', Utils.Mask.Money(redemptionValue));
    setValueApplied(redemptionValue);

    setIsFocused(false);
  };

  const handleInputBlur = async () => {
    const redemptionValue = getRedemptionValueOrMinimum();
    const redemptionValueFormatted = Utils.Mask.Money(redemptionValue);

    if (redemptionValue !== minValue) {
      if (isAbleToChange(redemptionValue)) {
        tableForm.setValue('redemptionValue', redemptionValueFormatted);
        setPrevValue(redemptionValue);
        setValueApplied(redemptionValue);
      }
    }

    if (isFocused) {
      setIsFocused(false);
    }
  };

  const handleInputFocus = () => {
    setIsFocused(true);
    setPrevValue(minValue);
    if (tableForm.formState.errors?.redemptionValue) {
      tableForm.resetField('redemptionValue');
    }
  };

  const handleInputClear = () => {
    setIsFocused(false);
    tableForm.resetField('redemptionValue');
    tableForm.clearErrors('redemptionValue');
  };

  useEffect(() => {
    setPrevValue(0);
    setValueApplied(minValue);
    setIsFocused(false);
  }, [minValue, maxValue]);

  return {
    isFocused,
    valueApplied,
    valueAppliedFormatted: Utils.Mask.Money(valueApplied),
    handleInputBlur,
    handleInputFocus,
    handleInputClear,
    handleSubmit: tableForm.handleSubmit(handleSubmit),
    control: tableForm.control as unknown as Control,
    hasError: tableForm.formState.errors?.redemptionValue,
  };
};
